/**
 * Roles, the permission matrix and the screen catalogue — `docs/API.md` §3.
 * Mirrors `salescrmapi/src/modules/roles`.
 */
import apiClient, { ifMatch, idempotencyKey } from './axiosClient.js';

/**
 * Every role the tenant can use: its own, plus the built-in templates shared
 * across tenants (`is_system: true`). Templates can be read and cloned but never
 * edited, renamed or deleted — the API answers `409` if you try.
 */
export async function listRoles() {
  const { data } = await apiClient.get('/roles');
  return data.data;
}

/** One role, with its assigned-user and permission counts. */
export async function getRole(id) {
  const { data } = await apiClient.get(`/roles/${id}`);
  return data;
}

/**
 * Create a tenant role, usually by cloning a template.
 *
 * `clone_from_role_id` copies the whole permission matrix and the field
 * permissions with it. Building two hundred grants by hand is not something
 * anyone does twice, so cloning is the expected path and starting empty is the
 * exception.
 *
 * @param {object} payload see CreateRoleRequest
 */
export async function createRole(payload) {
  const { data } = await apiClient.post('/roles', payload, { headers: idempotencyKey() });
  return data;
}

/**
 * Update a role's metadata — not its permissions, which have their own endpoint.
 * @param {string} id
 * @param {object} patch
 * @param {number} rowVersion
 */
export async function updateRole(id, patch, rowVersion) {
  const { data } = await apiClient.patch(`/roles/${id}`, patch, { headers: ifMatch(rowVersion) });
  return data;
}

/**
 * Delete a role.
 *
 * Refuses with `role_in_use` while any user holds it; `error.payload.holders`
 * lists them, so the UI can offer to reassign rather than just saying no.
 *
 * @param {string} id
 */
export async function deleteRole(id) {
  await apiClient.delete(`/roles/${id}`);
}

/**
 * The permission grid for one role.
 *
 * Each screen carries `available_operations` from `iam.screen_operation` — the
 * pairs that are legal at all — and `grants`, the subset this role holds. Render
 * checkboxes only for `available_operations`, so "delete on the Forecast page"
 * is never offered as a choice.
 *
 * @param {string} id
 */
export async function getRolePermissions(id) {
  const { data } = await apiClient.get(`/roles/${id}/permissions`);
  return data;
}

/**
 * Replace a role's entire permission matrix.
 *
 * PUT, and the **complete** set every time — not a patch. The admin screen sends
 * exactly what it is displaying, which means a grant cannot survive because
 * someone forgot to send its removal. That failure mode is invisible until an
 * audit finds it.
 *
 * The response carries `warnings`: a change that strands live work, such as
 * removing the last `quotations:approve` while approvals are pending, says so
 * here. Show them before claiming success.
 *
 * @param {string} id
 * @param {{grants:object[], field_permissions?:object[], reason?:string}} payload
 * @returns {Promise<{applied:number, added:number, removed:number, unchanged:number,
 *                    affected_users:number, warnings:string[]}>}
 */
export async function putRolePermissions(id, payload) {
  const { data } = await apiClient.put(`/roles/${id}/permissions`, payload);
  return data;
}

/**
 * The catalogue behind the permission grid: every screen, every verb, and which
 * pairs are legal. `/screens` and `/operations` return the same document — the
 * grid needs both halves at once, so fetch it once.
 */
export async function getCatalogue() {
  const { data } = await apiClient.get('/screens');
  return data;
}
